import { observable, action, makeObservable, computed, runInAction } from 'mobx';
import {
  PublicClientApplication,
  IPublicClientApplication,
  Configuration,
  LogLevel,
  SilentRequest,
} from '@azure/msal-browser';

export interface Environment {
  api: EnvironmentApi;
  appIds: EnvironmentAppIds;
  msal: EnvironmentMsal;
}

interface EnvironmentApi {
  // Smartlog Desktop Api
  baseApi: string;
  dmsBaseApi: string;
  smartlogBaseApi: string;
  backupBaseApi: string;

  // Smartlog Cloud Api
  smartlogCloudApi: string;
  fssCloudApi: string;
}

interface EnvironmentAppIds {
  // Smartlog Desktop Ids
  baseApi: string;
  dmsBaseApi: string;
  smartlogBaseApi: string;
  backupBaseApi: string;

  // Smartlog Cloud Ids
  smartlogCloudApi: string;
  fssCloudApi: string;
}

interface EnvironmentMsal {
  instance: string;
  tenant: string;
  clientId: string;
}

export class EnvironmentStore {
  environment: Environment = null;
  msal: IPublicClientApplication = null;
  vesselId: string = '';
  loaded = false;

  private redirectUri = window.location.origin;

  constructor() {
    makeObservable(this, {
      environment: observable,
      msal: observable,
      loaded: observable,
      vesselId: observable,
      environmentWithVesselId: computed,

      load: action,
      setVesselId: action,
    });
  }

  load = async () => {
    try {
      this.environment = await fetch(`${window.location.origin}/dynamic-configs/config.json`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }).then((response) => response.json());
      this.msal = new PublicClientApplication(this.getMsalConfig());
      runInAction(() => {
        this.loaded = true;
      });
    } catch {
      console.error('Environment not loaded');
    }
  };

  setVesselId(vesselId: string) {
    this.vesselId = vesselId;
  }

  get environmentWithVesselId(): EnvironmentApi {
    return Object.keys(this.environment.api).reduce((acc, key) => {
      acc[key] = this.environment.api[key] + `/${this.vesselId}`;
      return acc;
    }, {} as EnvironmentApi);
  }

  getMsalConfig(): Configuration {
    return {
      auth: {
        authority: this.environment.msal.instance + this.environment.msal.tenant,
        clientId: this.environment.msal.clientId,
        redirectUri: this.redirectUri,
        postLogoutRedirectUri: this.redirectUri,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message, containsPii) => {
            if (containsPii) {
              return;
            }
            switch (level) {
              case LogLevel.Error:
                console.error(message);
                return;
              case LogLevel.Info:
                console.info(message);
                return;
              case LogLevel.Verbose:
                console.debug(message);
                return;
              case LogLevel.Warning:
                console.warn(message);
                return;
              default:
                return;
            }
          },
        },
      },
    };
  }

  getAuthParameters(): SilentRequest {
    return {
      scopes: [`${this.environment.msal.clientId}/user_impersonation`],
    };
  }
}

export const environmentStore = new EnvironmentStore();
